import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import classes from './MyProfile.module.css';
import { sendPasswordResetEmail } from 'firebase/auth';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage, authEmail, updateFBEmail, deleteAccount, reauthenticate } from '../../config';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L11 18M5 12L11 6" stroke="#2c3030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UserIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" stroke="#808383" strokeWidth="1.8"/>
    <path d="M4 20C4 16.686 7.582 14 12 14C16.418 14 20 16.686 20 20" stroke="#808383" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

// Same limits/logic as HamburgerMenu.js's avatar upload — this page is a
// second, more discoverable place to change your photo, not a different
// implementation of it.
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png'];

const reauthErrorMessage = (err) => (
  err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential'
    ? 'Incorrect password.'
    : 'There was a problem confirming your password.'
);

const MyProfile = ({ onClose, settings }) => {
  const user = auth.currentUser;

  // Profile photo
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const handleAvatarClick = () => {
    if (uploadingPhoto) return;
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      alert('Please choose a JPG or PNG image.');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      alert('Please choose an image under 5MB.');
      return;
    }
    setUploadingPhoto(true);
    try {
      const photoRef = storageRef(storage, `users/${user.uid}/profile-photo`);
      await uploadBytes(photoRef, file);
      const url = await getDownloadURL(photoRef);
      await updateDoc(doc(db, 'users', user.uid), { photoURL: url });
    } catch (err) {
      alert('There was a problem uploading your photo.');
    }
    setUploadingPhoto(false);
  };

  // Display name
  const [name, setName] = useState(settings?.name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === settings?.name) return;
    setNameSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { name: trimmed });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2000);
    } catch (err) {
      alert('There was a problem saving your name.');
    }
    setNameSaving(false);
  };

  // Change email
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');

  const updateEmailHandler = async () => {
    if (!newEmail || !emailPassword) {
      setEmailMsg('Enter a new email and your current password.');
      return;
    }
    setEmailSaving(true);
    setEmailMsg('');
    try {
      const credential = authEmail.credential(user.email, emailPassword);
      await reauthenticate(user, credential);
      await updateFBEmail(user, newEmail);
      await updateDoc(doc(db, 'users', user.uid), { email: newEmail });
      setEmailMsg('Email updated.');
      setNewEmail('');
      setEmailPassword('');
    } catch (err) {
      setEmailMsg(reauthErrorMessage(err));
    }
    setEmailSaving(false);
  };

  // Change password
  const [passwordSending, setPasswordSending] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');

  const sendPasswordReset = () => {
    setPasswordSending(true);
    setPasswordMsg('');
    sendPasswordResetEmail(auth, user.email).then(() => {
      setPasswordMsg(`Check your email (${user.email}) for a reset link.`);
      setPasswordSending(false);
    }).catch(() => {
      setPasswordMsg('There was a problem sending the reset email.');
      setPasswordSending(false);
    });
  };

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletePassword('');
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!deletePassword) {
      setDeleteError('Enter your current password.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      const credential = authEmail.credential(user.email, deletePassword);
      await reauthenticate(user, credential);
      // Delete the Firestore profile before the auth account — the
      // Firestore rule requires request.auth.uid, which is gone the
      // instant deleteUser() succeeds (it signs the user out).
      await deleteDoc(doc(db, 'users', user.uid));
      await deleteAccount(user);
      // RootNavigator's onAuthStateChanged listener takes it from here
      // and falls back to the onboarding screen automatically.
    } catch (err) {
      setDeleteError(reauthErrorMessage(err));
      setDeleting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={classes.overlay}>
      <div className={classes.page}>

        <div className={classes.header}>
          <button className={classes.backBtn} onClick={onClose} aria-label="Go back">
            <BackIcon />
          </button>
          <span className={classes.title}>My Profile</span>
        </div>

        <div className={classes.content}>

          {/* Profile photo */}
          <div className={classes.card}>
            <label className={classes.label}>Profile photo</label>
            <div className={classes.photoRow}>
              <button
                className={classes.avatarBtn}
                onClick={handleAvatarClick}
                disabled={uploadingPhoto}
                aria-label="Change profile photo"
              >
                {settings?.photoURL ? (
                  <img src={settings.photoURL} alt="" className={classes.avatarImage} />
                ) : (
                  <UserIcon />
                )}
              </button>
              <div className={classes.photoActions}>
                <button className={classes.actionBtn} onClick={handleAvatarClick} disabled={uploadingPhoto}>
                  {uploadingPhoto ? 'Uploading...' : settings?.photoURL ? 'Change photo' : 'Upload photo'}
                </button>
                <p className={classes.photoHint}>JPG or PNG, up to 5MB</p>
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Display name */}
          <div className={classes.card}>
            <label className={classes.label}>Display name</label>
            <div className={classes.fieldRow}>
              <input
                className={classes.input}
                value={name}
                onChange={e => setName(e.target.value)}
                spellCheck="false"
              />
              <button className={classes.saveBtn} onClick={saveName} disabled={nameSaving}>
                {nameSaving ? 'Saving...' : nameSaved ? 'Saved ✓' : 'Save'}
              </button>
            </div>
          </div>

          {/* Change email */}
          <div className={classes.card}>
            <label className={classes.label}>Email</label>
            <div className={classes.currentValue}>{settings?.email || user?.email}</div>
            <input
              className={classes.input}
              type="email"
              placeholder="New email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              spellCheck="false"
            />
            <input
              className={classes.input}
              type="password"
              placeholder="Current password"
              value={emailPassword}
              onChange={e => setEmailPassword(e.target.value)}
            />
            {emailMsg && <p className={classes.msg}>{emailMsg}</p>}
            <button className={classes.actionBtn} onClick={updateEmailHandler} disabled={emailSaving}>
              {emailSaving ? 'Updating...' : 'Update email'}
            </button>
          </div>

          {/* Change password */}
          <div className={classes.card}>
            <label className={classes.label}>Password</label>
            {passwordMsg && <p className={classes.msg}>{passwordMsg}</p>}
            <button className={classes.actionBtn} onClick={sendPasswordReset} disabled={passwordSending}>
              {passwordSending ? 'Sending...' : 'Send password reset email'}
            </button>
          </div>

          {/* Delete account */}
          <div className={classes.deleteSection}>
            {!showDeleteConfirm ? (
              <button className={classes.deleteLink} onClick={() => setShowDeleteConfirm(true)}>
                Delete account
              </button>
            ) : (
              <div className={classes.card}>
                <p className={classes.deleteWarning}>
                  This permanently deletes your account and cannot be undone. Enter your password to confirm.
                </p>
                <input
                  className={classes.input}
                  type="password"
                  placeholder="Current password"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                />
                {deleteError && <p className={classes.msg}>{deleteError}</p>}
                <div className={classes.deleteActions}>
                  <button className={classes.cancelBtn} onClick={cancelDelete} disabled={deleting}>
                    Cancel
                  </button>
                  <button className={classes.deleteBtn} onClick={confirmDelete} disabled={deleting}>
                    {deleting ? 'Deleting...' : 'Delete account'}
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default MyProfile;
